import { PlusIcon } from '@shopify/polaris-icons';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Page, Card, BlockStack, Button, InlineStack, Select, Banner, DataTable } from '@shopify/polaris';
import { useTransferClient, useTransferRead, type TransferClient } from '../components/transfers/useTransferClient';
import { TransferFeedback } from '../components/transfers/TransferFeedback';
import type { Transfer } from '../components/transfers/types';
export default function TransferOrders() {
  const client = useTransferClient();
  return <TransferList key={client.key} client={client} />;
}
function TransferList({ client }: { client: TransferClient }) {
  const navigate = useNavigate();
  const [status, setStatus] = useState('all');
  const [cursor, setCursor] = useState<number | null>(null);
  const [refresh, setRefresh] = useState(0);
  const { data, error } = useTransferRead<{ transfers: Transfer[]; nextCursor: number | null; canEdit: boolean }>(
    client,
    `/?status=${status}${cursor ? '&cursor=' + cursor : ''}`,
    refresh,
  );
  return (
    <Page
      title="Warehouse transfers"
      subtitle="Plan and track stock moving between your warehouses"
      primaryAction={
        data?.canEdit
          ? { icon: PlusIcon, content: 'Create transfer', disabled: client.locked, onAction: () => navigate('/transfer-orders/new') }
          : undefined
      }
    >
      <BlockStack gap="400">
        <TransferFeedback client={client} />
        <Card>
          <BlockStack gap="300">
            <Select
              label="Status"
              value={status}
              onChange={(value) => {
                setStatus(value);
                setCursor(null);
              }}
              options={[
                'all',
                'active',
                'draft',
                'in_transit',
                'partially_received',
                'received',
                'closed',
                'cancelled',
                'legacy',
              ].map((value) => ({
                value,
                label: value === 'legacy' ? 'Older transfers needing review' : value.replace(/_/g, ' '),
              }))}
            />
            <Button onClick={() => setRefresh((x) => x + 1)}>Refresh transfers</Button>
            {error && <Banner tone="critical">{error}</Banner>}
            {!data && !error && <p>Loading transfers…</p>}
            {data && (
              <>
                <DataTable
                  columnContentTypes={['text', 'text', 'text', 'text', 'numeric', 'numeric', 'text']}
                  headings={['Reference', 'From', 'To', 'Status', 'Requested', 'In transit', 'Action']}
                  rows={data.transfers.map((t) => [
                    t.reference,
                    t.from_warehouse_name,
                    t.to_warehouse_name,
                    t.lifecycle_version !== 1 ? 'Older record: review needed' : t.status.replace(/_/g, ' '),
                    t.requested,
                    t.shipped - t.received - t.damaged - t.lost - t.returned,
                    <Button key={t.id} onClick={() => navigate('/transfer-orders/' + t.id)}>
                      View {t.reference}
                    </Button>,
                  ])}
                />
                {!data.transfers.length && <p>No transfers match this page and filter.</p>}
                <p>
                  Up to 50 transfers per page. Shopify is optional. Physical stock changes queue inventory updates when
                  a store is connected.
                </p>
                <InlineStack gap="200">
                  <Button disabled={!cursor} onClick={() => setCursor(null)}>
                    First page
                  </Button>
                  <Button disabled={!data.nextCursor} onClick={() => setCursor(data.nextCursor)}>
                    Next page
                  </Button>
                </InlineStack>
              </>
            )}
          </BlockStack>
        </Card>
      </BlockStack>
    </Page>
  );
}
